import { TPoint, TRect, TSize } from '@src/types/misc';

export function centerImage(
  imageDimensions: TSize | Record<string, never>,
  clippingPathInformation: TSize | Record<string, never>
): TPoint {
  const clipMidX = clippingPathInformation.width / 2;
  const clipMidY = clippingPathInformation.height / 2;
  const imageMidX = imageDimensions.width / 2;
  const imageMidY = imageDimensions.height / 2;

  const x = clipMidX - imageMidX;
  const y = clipMidY - imageMidY;

  return { x, y };
}

export function constrainedImageLocation(
  clipLocationInViewbox: TRect,
  hopefulPosition: TRect
): TRect {
  const newPosition = { ...hopefulPosition };
  const clipX = 0;
  const clipY = 0;
  const clipHeight = clipLocationInViewbox.height;
  const clipWidth = clipLocationInViewbox.width;

  if (clipY < newPosition.y) {
    newPosition.y = clipY;
  }

  if (clipX < newPosition.x) {
    newPosition.x = clipX;
  }

  if (clipY + clipHeight > newPosition.y + newPosition.height) {
    newPosition.y = clipY + clipHeight - newPosition.height;
  }

  if (clipX + clipWidth > newPosition.x + newPosition.width) {
    newPosition.x = clipX + clipWidth - newPosition.width;
  }

  return newPosition;
}

export function convertRectToViewboxPercentages(
  rect: TRect,
  viewbox: TSize
): TRect {
  return {
    height: rect.height / viewbox.height,
    width: rect.width / viewbox.width,
    x: rect.x / viewbox.width,
    y: rect.y / viewbox.height
  };
}

export function convertSizeToViewboxPercentages(
  size: TSize,
  viewbox: TSize
): TSize {
  return {
    height: size.height / viewbox.height,
    width: size.width / viewbox.width
  };
}

export function convertSizeToRenderPercentages(
  size: TSize,
  renderRect: TSize
): TSize {
  return {
    height: size.height / renderRect.height,
    width: size.width / renderRect.width
  };
}

export function getMinimumDimensionsFromImageDimensionsToCoverArea(
  imageLocation: TRect | Record<string, never>,
  areaToCover: TRect | Record<string, never>
): TSize {
  const imageHeightWidthRatio = imageLocation.height / imageLocation.width;
  let minimumHeight = areaToCover.height;
  let minimumWidth = minimumHeight / imageHeightWidthRatio;

  if (minimumWidth < areaToCover.width) {
    minimumWidth = areaToCover.width;
    minimumHeight = minimumWidth * imageHeightWidthRatio;
  }

  return { height: minimumHeight, width: minimumWidth };
}

export function getMinimumDimensionsFromImageElToCoverArea(
  areaToCover: TSize | Record<string, never>,
  image: HTMLImageElement
): TSize {
  const naturalHeight = image.naturalHeight;
  const naturalWidth = image.naturalWidth;
  const heightWidthRatio = naturalHeight / naturalWidth;
  const minimumDimensions = {
    height: areaToCover.height,
    width: areaToCover.height / heightWidthRatio
  };

  if (minimumDimensions.width < areaToCover.width) {
    minimumDimensions.width = areaToCover.width;
    minimumDimensions.height = areaToCover.width * heightWidthRatio;
  }

  minimumDimensions.height = Math.ceil(minimumDimensions.height);
  minimumDimensions.width = Math.ceil(minimumDimensions.width);

  return minimumDimensions;
}

export function getMinimumDimensionsFromNaturalDimensionsToCoverArea(
  areaToCover: TSize | Record<string, never>,
  naturalDimensions: TSize
): TSize {
  const naturalHeight = naturalDimensions.height;
  const naturalWidth = naturalDimensions.width;
  const heightWidthRatio = naturalHeight / naturalWidth;
  const minimumDimensions = {
    height: areaToCover.height,
    width: areaToCover.height / heightWidthRatio
  };

  if (minimumDimensions.width < areaToCover.width) {
    minimumDimensions.width = areaToCover.width;
    minimumDimensions.height = areaToCover.width * heightWidthRatio;
  }

  minimumDimensions.height = Math.ceil(minimumDimensions.height);
  minimumDimensions.width = Math.ceil(minimumDimensions.width);

  return minimumDimensions;
}
